import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Clock, Package, MapPin, Shield, FileText, XCircle } from 'lucide-react';

const ShippingDeliveryPolicyPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Shipping & Delivery Policy | Strum Music";
  }, []);

  const sideNavItems = [
    {
      title: 'Privacy Policy',
      href: '/privacy-policy',
      icon: <Shield className="h-4 w-4" />
    },
    {
      title: 'Terms & Conditions',
      href: '/terms-conditions',
      icon: <FileText className="h-4 w-4" />
    },
    {
      title: 'Shipping & Delivery Policy',
      href: '/shipping-delivery-policy',
      icon: <Truck className="h-4 w-4" />
    },
    {
      title: 'Cancellation & Refund Policy',
      href: '/cancellation-refund-policy',
      icon: <XCircle className="h-4 w-4" />
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-blue-700 py-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Truck className="h-16 w-16 mx-auto mb-4 text-blue-200" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Shipping & Delivery Policy</h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              We are committed to delivering your gear quickly and safely
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-12">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Policies</h2>
                <nav className="space-y-2">
                  {sideNavItems.map((item, index) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={index}
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-700'
                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                        }`}
                      >
                        {item.icon}
                        <span>{item.title}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-8"
              >
                {/* Overview */}
                <div>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    At Strum Music, we are committed to delivering your musical instruments and accessories quickly, 
                    safely, and at competitive shipping rates. This policy outlines our shipping and delivery terms.
                  </p>
                  <p className="text-gray-600 text-sm">
                    <strong>Last updated:</strong> January 2025
                  </p>
                </div>

                {/* Service Area */}
                <div>
                  <div className="flex items-center mb-4">
                    <MapPin className="h-6 w-6 text-blue-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Service Area</h2>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <p className="text-gray-700 mb-4">
                      Currently, we ship to all major cities and towns across <strong>India</strong>. 
                      For international shipping, please contact us directly at{' '}
                      <a href="mailto:strum.music.co@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium">
                        strum.music.co@gmail.com
                      </a>.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-blue-800 mb-2">Major Cities</h4>
                        <p className="text-blue-700 text-sm">Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Pune, Ahmedabad</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-blue-800 mb-2">Coverage</h4>
                        <p className="text-blue-700 text-sm">All PIN codes serviceable by our courier partners</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Timeline */}
                <div>
                  <div className="flex items-center mb-4">
                    <Clock className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Delivery Timeline</h2>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-green-800 mb-3">Processing Time</h4>
                        <ul className="space-y-2 text-green-700">
                          <li>• <strong>In-stock items:</strong> 1-2 working days</li>
                          <li>• <strong>Custom orders:</strong> 5-7 working days</li>
                          <li>• <strong>Rental items:</strong> Same day (Delhi NCR)</li>
                          <li>• <strong>Open box items:</strong> 1-2 working days</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-green-800 mb-3">Delivery Time</h4>
                        <ul className="space-y-2 text-green-700">
                          <li>• <strong>Metro cities:</strong> 3-5 working days</li>
                          <li>• <strong>Tier 2 cities:</strong> 4-6 working days</li>
                          <li>• <strong>Remote areas:</strong> 5-7 working days</li>
                          <li>• <strong>Express delivery:</strong> 1-2 working days (available in select cities)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Charges */}
                <div>
                  <div className="flex items-center mb-4">
                    <Package className="h-6 w-6 text-purple-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Shipping Charges</h2>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-800 mb-3">Standard Shipping</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <ul className="space-y-2 text-purple-700">
                          <li>• <strong>FREE shipping</strong> for orders above ₹9,999</li>
                          <li>• <strong>Instruments:</strong> ₹500 shipping charge</li>
                          <li>• <strong>Accessories:</strong> ₹100 shipping charge</li>
                        </ul>
                        <ul className="space-y-2 text-purple-700">
                          <li>• <strong>Express delivery:</strong> Additional ₹200</li>
                          <li>• <strong>Remote areas:</strong> Additional ₹100</li>
                          <li>• <strong>Rental delivery:</strong> ₹300 (Delhi NCR)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-600">
                      <h4 className="font-medium text-yellow-800 mb-2">Weight-based Charges</h4>
                      <p className="text-yellow-700 text-sm">
                        For heavy instruments (above 25kg), additional charges may apply based on volumetric weight. 
                        You will be notified before dispatch if additional charges are required.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tracking */}
                <div>
                  <div className="flex items-center mb-4">
                    <Shield className="h-6 w-6 text-orange-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Order Tracking</h2>
                  </div>
                  <div className="bg-orange-50 p-6 rounded-lg">
                    <ul className="space-y-3 text-gray-700">
                      <li>• Tracking links will be sent via SMS and email once the item is shipped</li>
                      <li>• Real-time tracking available through our courier partners</li>
                      <li>• SMS updates at key delivery milestones</li>
                      <li>• Customer support available for tracking queries</li>
                    </ul>
                  </div>
                </div>

                {/* Packaging */}
                <div>
                  <div className="flex items-center mb-4">
                    <Package className="h-6 w-6 text-teal-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Packaging Standards</h2>
                  </div>
                  <div className="bg-teal-50 p-6 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-teal-800 mb-3">Instrument Protection</h4>
                        <ul className="space-y-2 text-teal-700">
                          <li>• Bubble wrap and foam padding</li>
                          <li>• Sturdy cardboard boxes</li>
                          <li>• Fragile item stickers</li>
                          <li>• Climate-resistant packaging</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-teal-800 mb-3">Eco-Friendly Materials</h4>
                        <ul className="space-y-2 text-teal-700">
                          <li>• Recyclable packaging materials</li>
                          <li>• Biodegradable void fill</li>
                          <li>• Minimal plastic usage</li>
                          <li>• Reusable packaging when possible</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Process */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Delivery Process</h2>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    {[
                      {
                        step: "1",
                        title: "Order Confirmation",
                        description: "You'll receive an order confirmation email with estimated delivery date.",
                        icon: "📧"
                      },
                      {
                        step: "2", 
                        title: "Dispatch Notification",
                        description: "SMS and email notification when your order is shipped with tracking details.",
                        icon: "📦"
                      },
                      {
                        step: "3",
                        title: "Delivery Attempt",
                        description: "Our delivery partner will attempt delivery and may call for coordination.",
                        icon: "🚚"
                      }
                    ].map((step, index) => (
                      <div key={index} className="text-center p-6 border border-gray-200 rounded-lg">
                        <div className="text-4xl mb-4">{step.icon}</div>
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                          {step.step}
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                        <p className="text-gray-600 text-sm">{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-600">
                  <h3 className="text-lg font-semibold text-orange-800 mb-3">Important Delivery Notes</h3>
                  <ul className="space-y-2 text-orange-700">
                    <li>• Delivery times may vary during peak seasons and festivals</li>
                    <li>• Signature confirmation required for high-value items (above ₹25,000)</li>
                    <li>• Weather conditions may affect delivery schedules</li>
                    <li>• Ensure someone is available to receive the delivery</li>
                    <li>• Inspect the package before accepting delivery</li>
                    <li>• Report any damage immediately to our customer service</li>
                  </ul>
                </div>

                {/* Failed Delivery */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Failed Delivery Attempts</h2>
                  
                  <div className="bg-red-50 p-6 rounded-lg">
                    <ul className="space-y-3 text-gray-700">
                      <li>• <strong>First attempt:</strong> SMS notification sent, second attempt scheduled</li>
                      <li>• <strong>Second attempt:</strong> Customer contacted directly for coordination</li>
                      <li>• <strong>Third attempt:</strong> Package held at local courier facility for pickup</li>
                      <li>• <strong>After 7 days:</strong> Package returned to Strum Music, customer charged return shipping</li>
                    </ul>
                  </div>
                </div>

                {/* Contact for Shipping */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Shipping Support</h2>
                  <p className="text-gray-700 mb-4">
                    Our customer service team is here to help with any shipping questions or concerns.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a 
                      href="tel:+918076883098" 
                      className="btn btn-primary py-2 px-4 text-center flex items-center justify-center space-x-2"
                    >
                      <span>📞 Call: +91-8076883098</span>
                    </a>
                    <a 
                      href="mailto:strum.music.co@gmail.com" 
                      className="btn btn-outline py-2 px-4 text-center flex items-center justify-center space-x-2"
                    >
                      <span>✉️ Email: strum.music.co@gmail.com</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShippingDeliveryPolicyPage;